import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getDashboardMetricCards } from '@/api/dashboard'
import { pushNotification, useAppDispatch } from '@/store'
import { DashboardMetricCardsResponseModel } from '@/types/dashboard'
import { areFilterParamsValid } from '@/utils/filterParams'
import { DateType } from '@/utils'

export default function useDashboardMetricCards(
  startDate: string,
  endDate: string,
  branches: string,
  dateType: DateType
) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const isReady = useMemo(
    () => areFilterParamsValid(startDate, endDate, branches),
    [startDate, endDate, branches]
  )
  const loading = !isReady || isFetching

  const [metricCards, setMetricCards] =
    useState<DashboardMetricCardsResponseModel>()

  const fetchAllData = useCallback(async () => {
    if (!isReady) {
      setIsFetching(false)
      return
    }
    setIsFetching(true)
    try {
      const metricCardsResponse = await getDashboardMetricCards(
        branches,
        startDate,
        endDate,
        dateType
      )

      setMetricCards(metricCardsResponse)
    } catch (err) {
      const error = new Error(
        err instanceof Error ? err.message : 'An unknown error occurred'
      )

      dispatch(
        pushNotification({
          type: 'error',
          title: t('common.errors.serverError'),
          description: error.message,
          duration: 5000,
        })
      )
    } finally {
      setIsFetching(false)
    }
  }, [isReady, startDate, endDate, branches, dateType])

  const refetch = useCallback(() => {
    console.log('Refetching dashboard metric cards data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    metricCards,
    loading,
    refetch,
  }
}
